import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Loader2, RefreshCw, Search, X } from 'lucide-react';
import type { CommunicationsWebhookLog } from '../../../models/MasterAdmin/communications';
import {
  commAlertOverlayClass,
  commDesktopTableWrapClass,
  commMobileCardClass,
  commSecondaryBtnClass,
  commSelectClass,
  commToolbarClass,
} from '../../../components/Communications/communicationsUi';
import { getCommunicationsWebhookLogs } from '../../../services/MasterAdmin/communicationsService';

const DAYS_OPTIONS = [7, 30, 90];

function formatAction(action: string | null): string {
  if (!action) return '—';
  const labels: Record<string, string> = {
    whatsapp_requested: 'WhatsApp requested',
    ivr_disabled: 'IVR disabled',
    no_action: 'No action',
    outbound_initiated: 'Outbound initiated',
    status_callback: 'Status update',
    answered: 'Answered',
    terminal: 'Completed',
  };
  return labels[action] || action;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function WebhookPayloadDialog({
  row,
  onClose,
}: {
  row: CommunicationsWebhookLog;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const payload = row.rawPayload ?? {};

  return (
    <div
      className={commAlertOverlayClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="webhook-payload-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 sm:max-w-2xl sm:rounded-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <div className="min-w-0">
            <h3 id="webhook-payload-title" className="text-base font-semibold text-gray-900 dark:text-white">
              Webhook payload
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {row.source || 'webhook'} · {formatAction(row.actionTaken)} ·{' '}
              {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">
          <pre className="overflow-x-auto rounded-lg bg-gray-950 p-4 text-xs leading-relaxed text-green-200">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </div>
        <div className="shrink-0 border-t border-gray-100 px-5 py-4 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const CommunicationsWebhooksPanel: React.FC = () => {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<CommunicationsWebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [viewRow, setViewRow] = useState<CommunicationsWebhookLog | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCommunicationsWebhookLogs(days, 200);
      setRows(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load webhook logs.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return rows;
    const q = searchText.toLowerCase();
    return rows.filter(
      (r) =>
        (r.callSid || '').toLowerCase().includes(q) ||
        (r.fromNumber || '').toLowerCase().includes(q) ||
        (r.toNumber || '').toLowerCase().includes(q) ||
        (r.source || '').toLowerCase().includes(q) ||
        (r.actionTaken || '').toLowerCase().includes(q) ||
        (r.eventType || '').toLowerCase().includes(q) ||
        (r.callStatus || '').toLowerCase().includes(q)
    );
  }, [rows, searchText]);

  const renderViewButton = (row: CommunicationsWebhookLog) => (
    <button
      type="button"
      onClick={() => setViewRow(row)}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
      title="View JSON payload"
    >
      <Eye className="h-3.5 w-3.5" />
      View
    </button>
  );

  return (
    <div className="space-y-6">
      {viewRow ? <WebhookPayloadDialog row={viewRow} onClose={() => setViewRow(null)} /> : null}

      <div className={commToolbarClass}>
        <label className="flex w-full items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:w-auto">
          <span className="shrink-0">Range</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={commSelectClass}>
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => void load()} disabled={loading} className={commSecondaryBtnClass}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 p-3 dark:border-gray-700 sm:p-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search webhook logs by SID, phone, source, status…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-base dark:border-gray-600 dark:bg-gray-900 dark:text-white sm:text-sm"
            />
          </div>
        </div>

        <div className="space-y-3 p-3 md:hidden">
          {filteredRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {loading ? 'Loading…' : 'No webhook events in this range.'}
            </p>
          ) : (
            filteredRows.map((row) => (
              <div key={row.id} className={commMobileCardClass}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase text-gray-500">Source</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{row.source || 'webhook'}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {row.callStatus || '—'}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">From</p>
                    <p className="break-all text-gray-700 dark:text-gray-300">{row.fromNumber || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">To</p>
                    <p className="break-all text-gray-700 dark:text-gray-300">{row.toNumber || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Action</p>
                    <p className="text-gray-700 dark:text-gray-300">{formatAction(row.actionTaken)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Duration</p>
                    <p className="tabular-nums text-gray-900 dark:text-white">{formatDuration(row.duration)}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                </p>
                <p className="mt-1 truncate font-mono text-xs text-gray-400">{row.callSid || '—'}</p>
                <div className="mt-3">{renderViewButton(row)}</div>
              </div>
            ))
          )}
        </div>

        <div className={commDesktopTableWrapClass}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                {['Source', 'Action', 'From', 'To', 'Status', 'Duration', 'Received', ''].map((h) => (
                  <th
                    key={h || 'actions'}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                    {loading ? 'Loading…' : 'No webhook events in this range.'}
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.source || 'webhook'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>{formatAction(row.actionTaken)}</div>
                      {row.eventType ? (
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{row.eventType}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{row.fromNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{row.toNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{row.callStatus || '—'}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">
                      {formatDuration(row.duration)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      <div>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</div>
                      <div className="mt-0.5 max-w-[12rem] truncate font-mono text-gray-500">{row.callSid || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-right">{renderViewButton(row)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CommunicationsWebhooksPanel;
